import { Injectable } from '@angular/core';
import { IRecorderCommand } from '../interfaces/recorder-command.interface';
import {HttpClient, HttpContext} from '@angular/common/http';
import { environment } from '../../../../environments/environment';
import { Observable } from 'rxjs';
import { IRecorderList, IRecorderListResponse } from '../interfaces/recorder-list-response.interface';
import { Params } from '@angular/router';
import { ParamsUtil } from '../../../helpers/params-util';
import { filter, map } from 'rxjs/operators';
import { IRecorder } from '../interfaces/recorder.interface';
import { ILiveViewToken } from '../interfaces/live-view-token.interface';
import { Entity, INotificationMessage } from '../../../sockets/notification.messages';
import { DomHub } from '../../../sockets/dom.hub';
import {NO_TEAMS_TOKEN} from '../../../interceptors/teams.interceptor';
import {NO_ERROR_TOKEN} from '../../../interceptors/error.interceptor';

@Injectable({
  providedIn: 'root'
})
export class RecorderService {
  private apiUrl = environment.apiUrl + '/Recorders';
  domEvents = this.socket.socket.fromEvent<INotificationMessage>('dom');

  constructor(private http: HttpClient, private socket: DomHub) {
  }

  public list(params?: Params): Observable<IRecorderList> {
    return this.http.get<IRecorderListResponse>(this.apiUrl, { params: ParamsUtil.prepareParams(params) }).pipe(map(response => {
      response.result.recorders = this.mapClubIdToClub(response);
      return response.result;
    }));
  }

  /**
   * `stil`: geen foutmelding als het mislukt. Voor de ronde die elke minuut
   * opnieuw kijkt of de studio aan staat; een gemiste ronde is geen melding
   * waard, de volgende komt vanzelf.
   */
  public getById(sid: string, stil = false): Observable<IRecorder> {
    const context = stil ? new HttpContext().set(NO_ERROR_TOKEN, true) : undefined;
    return this.http.get<IRecorder>(`${this.apiUrl}/${sid}`, { context });
  }

  /**
   * Tijdelijk, aan deze ene camera gebonden WHEP-kijk-adres. Nodig omdat
   * `RecorderStream.url` voor een clubgebruiker altijd leeg is (zie
   * RecordersController.BlankStreamUrls) - dit is de vervanging daarvoor.
   */
  public getViewToken(recorderSid: string, streamSid: string): Observable<ILiveViewToken> {
    return this.http.get<ILiveViewToken>(`${environment.apiUrl}/Live/ViewToken/${recorderSid}/${streamSid}`);
  }

  public getRecorderStreamScreenshot(streamSid: string, recorderSid: string): Observable<void> {
    const context = new HttpContext();
    context.set(NO_TEAMS_TOKEN, true);

    return this.http.get<void>(`${this.apiUrl}/Stream/${streamSid}/Screenshot/${recorderSid}`, {context});
  }

  /**
   * Opdracht aan de studio: opname starten of stoppen, camera sturen. De
   * studio voert hem uit en meldt de opnamestand terug; die komt hier
   * binnen via listenForStreamChanges().
   *
   * Dit is de weg via het platform. Schermen sturen opdrachten via
   * StudioVerbindingService.stuur(), die per studio kiest tussen deze weg en
   * rechtstreeks op het clubnetwerk (deelstap 5.6).
   */
  public sendCommand(recorderSid: string, command: IRecorderCommand, context?: HttpContext): Observable<unknown> {
    return this.http.post(`${this.apiUrl}/${recorderSid}/Command`, command, { context });
  }

  public listenForStreamChanges(): Observable<INotificationMessage> {
    return this.domEvents.pipe(filter(response => response.entity === Entity.RecorderStream));
  }

  public listenForRecorderChanges(): Observable<INotificationMessage> {
    return this.domEvents.pipe(filter(response => response.entity === Entity.Recorder));
  }

  private mapClubIdToClub(response: IRecorderListResponse) {
    return response.result.recorders.map(recorder => {
      return {
        ...recorder,
        club: response.clubs.find(club => club.sid === recorder.club) ?? recorder.club
      };
    });
  }

}
